/**
 * @fileoverview SettingsAppMenu — left-rail nav of the SettingsAppShell.
 * Renders one or more SettingsAppMenuCategory groups, each with a muted
 * header and a stack of SettingsAppMenuItem links.
 *
 * Foundry mapping: `settings-app__sidebar.settings-app__app-menu.bp6-menu`
 * with `settings-app__menu-category` groups. The menu is a `<ul role="menu">`
 * containing the categories; each category opens with a `bp6-menu-header`.
 */

import type { ReactNode } from "react";
import { Link } from "react-router";
import { controlPanelShell as fdy } from "../control-panel/foundry-parts.generated";

interface SettingsAppMenuProps {
  /** SettingsAppMenuCategory groups. */
  children: ReactNode;
  className?: string;
}

export function SettingsAppMenu({ children, className }: SettingsAppMenuProps) {
  return (
    /*
      THIS UL IS THE RAIL ITSELF. It used to be a plain `bp6-menu` inside a
      `div.__settings-sidebar__` supplied by the shell; the capture has one
      element wearing both `__settings-sidebar__` and `__sidebar__`, so the
      wrapper is gone and the classes live where the DOM puts them.
    */
    <ul data-testid="settings-app-menu"
      role="menu"
      className={[fdy["settings-sidebar"], fdy["sidebar"], "bp6-menu", "bp-settings-app-menu", className].filter(Boolean).join(" ")}
    >
      {children}
    </ul>
  );
}

interface SettingsAppMenuCategoryProps {
  /** Header label rendered above the category's items. */
  title: string;
  /** SettingsAppMenuItem links. */
  children: ReactNode;
  className?: string;
}

export function SettingsAppMenuCategory({
  title,
  children,
  className,
}: SettingsAppMenuCategoryProps) {
  return (
    /*
      A SECTION IS A `ul`, NOT A `div`. This was a div holding an li, which is
      invalid inside the rail's own ul and does not match the capture — the
      tenant nests `ul.__sidebar-section__` directly, and the diff put an
      unexpected div at every group. Nested ul-in-ul is itself outside the spec
      (a ul's only permitted children are li), but it is what Foundry ships and
      the classes are theirs; reproducing it is the point of the gate.
    */
    <ul
      className={[fdy["sidebar-section"], "bp-settings-app-menu-category", className]
        .filter(Boolean)
        .join(" ")}
    >
      <li className="bp6-menu-header bp-settings-app-menu-header">{title}</li>
      {children}
    </ul>
  );
}

interface SettingsAppMenuItemProps {
  href: string;
  label: string;
  active?: boolean;
  className?: string;
}

export function SettingsAppMenuItem({
  href,
  label,
  active = false,
  className,
}: SettingsAppMenuItemProps) {
  return (
    // Wrapped in an li: the rail is a list, and an anchor as a direct child of
    // a ul is neither valid nor what the capture has.
    <li>
    <Link
      to={href}
      aria-current={active ? "page" : undefined}
      className={[
        "bp6-menu-item",
        "bp-settings-app-menu-item",
        active ? "bp6-active is-active" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div>{label}</div>
    </Link>
    </li>
  );
}
